/**
 * A module representing data context.
 * @module data-context
 * @exports DataContext
 * @see DataContext
 * @requires knockout
 * @requires utils
 * @requires helpers
 * @requires get-learning
 * @requires send-progress
 * @requires load-modules
 * @requires training
 * @requires notification
 * @requires user-answers
 * @requires view-model
 * @requires navigation
 * @example
 * var DataContext = require('data-context');
 */
define('data-context',['require', 'knockout', 'utils', 'helpers', 'get-learning', 'load-modules', 'training', 'user-answers', 'view-model', 'navigation', 'notification', 'store', 'pubsub'],
        function(require, ko, utils, helpers, getLearning, loadModules, Training, UserAnswers, ViewModel, Navigation, Notification, Store, PubSub) {

    'use strict';

    var store = Store.default;

    var bindViewModel = function(dataContext) {
        var root = document.documentElement,
            viewModel = new ViewModel(dataContext),
            navigation = new Navigation(),
            training = new Training();

        navigation.setViewModel(viewModel);

        if (!dataContext.learning.current) {

            if (dataContext.debugMode) {
                console.error('Cloned init entity doesn\'t exist or is empty');
            }

            return;
        }

        PubSub.publish('page-load-init', {
            entity: dataContext.learning.current,
            state: training.state
        });

        ko.applyBindings(viewModel);

        PubSub.publish('transition-in-init', {});

        window.viewModel = viewModel;

        var completeLoad = function() {
            var loader = document.getElementById('loader');

            if (loader) {
                document.body.removeChild(loader);
            }

            PubSub.publish('transition-in-complete', {});

            document.getElementById('player').removeEventListener('transitionend', completeLoad, false);
        }

        document.getElementById('player').addEventListener('transitionend', completeLoad, false);

        root.classList.remove('loading');

        if (dataContext.debugMode) {
            console.dir(dataContext);
        }
    };


    var dataContext,
        logPromise;

    /**
     * @constructor
     * @alias DataContext
     * @param {Object}  options
     * @param {String}  [options.autoIndentSVGBullets = false] - Are SVG bullets auto indented
     * @param {String}  [options.classID = URL classId] - class ID

     * @param {Object}  [options.commentTool = {}] - Comment tool settings
     * @param {Boolean} [options.commentTool.enabled = false] - Is comment tool enabled
     * @param {String}  [options.commentTool.url = false] - Comment tool URL

     * @param {Object}  [options.connection = {}] - Connection alert settings
     * @param {Boolean} [options.connection.modal = false] - Is connection alerts enabled
     * @param {Integer} [options.connection.delay = 0] - Delay before modal close (0 - doesn't close)
     * @param {Boolean} [options.connection.close = false] - Show close icon in modal

     * @param {String}  [options.dataURL = ''] - API method getgfptraining URL
     * @param {String}  [options.dateModified = ''] - Course date modified
     * @param {Boolean} [options.debugMode = false] - Is course in debug mode
     * @param {String}  [options.diplomaURL = ''] - API method PdfDiploma URL
     * @param {String}  [options.entityURL = ''] - API method GetContentEntity URL

     * @param {String}  [options.commentsCountURL = ''] - API method GetCommentsCountForContentEntity URL

     * @param {Integer} [options.forwardDelay = 1000] - Delay in ms before forwarding

     * @param {Object}  [options.layout = {}] - Layout settings
     * @param {String}  [options.layout.type = ''] - Layout type (default or fixed)
     * @param {Integer} [options.layout.width = 1180] - Layout width for fixed layout
     * @param {Integer} [options.layout.height = 720] - Layout height for fixed layout
     * @param {Boolean} [options.layout.landscape = false] - Disable landscape mode use of mobile/ tablet
     * @param {Object}  [options.layout.expandable] - Expandable layout settings
     * @param {Object}  [options.layout.elements] - Default layout elements structure
     * @param {Object}  [options.layout.elementsContentPage] - Default layout elements structure for content pages
     * @param {Object}  [options.layout.sortToGroups] - Default layout elements structure for content pages
     * @param {Integer} [options.layout.columnWidthAdjustment = 0] - Layout columns width adjustment
     * @param {Object}  [options.layout.mobile] - Mobile view structure settings

     * @param {Boolean} [options.livereload = false] - Enable livereload
     * @param {Boolean} [options.localStorage = false] - Player use of localStorage
     * @param {String}  [options.logURL = ] - API method LogLessonProgress URL

     * @param {String}  [options.mediaURL = ''] - API method Media URL

     * @param {Object}  [options.modules = {}] - Modules load settings
     * @param {Boolean} [options.modules.localization = false] - Forcing custom-lang.js load
     * @param {Array}   [options.modules.required = []] - Modules to load before binding
     * @param {Array}   [options.modules.optional = []] - Modules to load after binding

     * @param {Object}  [options.navigation = {}] - Navigation settings
     * @param {String}  [options.navigation.transition = 'none'] - Transition type (none, CSS or TweenMax)
     * @param {Integer} [options.navigation.duration = 0] - Transition duration in ms
     * @param {Object}  [options.navigation.swiping = {}] - Navigation swiping settings
     * @param {Boolean} [options.navigation.swiping.touchDevices = true] - Enable swiping navigation for touch devices
     * @param {Boolean} [options.navigation.swiping.nonTouchDevices = false] - Enable swiping navigation for nontouch devices

     * @param {Boolean} [options.offlinePackage = false] - Offline package
     * @param {Integer} [options.preloadDelay = 0] - Content preload delay in ms

     * @param {Boolean} [options.previewMode = false] - Is course in preview mode
     * @param {Boolean} [options.productionMode = false] - Is course in production mode

     * @param {Object}  [options.scormPackage = {}] - Scorm package settings
     * @param {Boolean} [options.scormPackage.enabled = false] - Is scorm package enabled
     * @param {String}  [options.scormPackage.lessonStatus = ''] - Lesson status to set after complete (default, completed or passed)
     * @param {String}  [options.scormPackage.completionTrackingType = ''] - Tracking lesson type (default, quiz or progress)
     * @param {String}  [options.scormPackage.version = ''] - Version type to use (auto, 1.2, 2004)

     * @param {String}  [options.setScoreURL = ''] - API method setScore URL
     * @param {String}  [options.setStatusURL = ''] - API method setStatus URL
     * @param {String}  [options.statisticsURL = ''] - API method getTaskStatistics URL

     * @param {Boolean} [options.svgsInIndexHtml = false] - Is svg in index.html
     * @param {Object}  [options.task = {}] - Tasks settings
     * @param {String}  [options.trainingID = ''] - Training ID

     * @param {String}  [options.version = ''] - Player version
     */

    function DataContext(options) {
        var _this = this;

        options = options || {};

        /** @name DataContext#autoIndentSVGBullets - Are SVG bullets auto indented */
        this.autoIndentSVGBullets = options.autoIndentSVGBullets || false;

        /** @name DataContext#classID - Class ID */
        this.classID = options.classID || helpers.URL.getParam('classId');

        /** @name DataContext#comments - Is comments tool enabled */
        this.comments = false;

        /** @name DataContext#commentTool - Comment tool settings */
        this.commentTool = options.commentTool || {};

        /** @name DataContext#connection - Connection alert settings */
        this.connection = options.connection || {};

        /** @name DataContext#dataURL - Data URL */
        this.dataURL = options.dataURL || '';

        /** @name DataContext#dateModified - Course date modified */
        this.dateModified = options.dateModified || '';

        /** @name DataContext#debugMode - Is debug mode */
        this.debugMode = !!options.debugMode || false;

        /** @name DataContext#diplomaURL - Diploma URL */
        this.diplomaURL = options.diplomaURL || '';

        /** @name DataContext#errorTimeoutID - Error timeout ID in log progress */
        this.errorTimeoutID = false;

        /** @name DataContext#entityURL - Entity data URL */
        this.entityURL = options.entityURL || '';

        /** @name DataContext#commentsCountURL - Entity data URL */
        this.commentsCountURL = options.commentsCountURL || '';

        /** @name DataContext#expandable - Is layout expandable */
        this.expandable = false;

        /** @name DataContext#expandable - Expandable layout delay */
        this.expandDelay = 0;

        /** @name DataContext#forwardDelay - Delay before forwarding after submit (if entity has no feedback and contains nextNavigateTo) */
        this.forwardDelay = (utils.isNumber(options.forwardDelay) && options.forwardDelay >= 0) ? options.forwardDelay : 1000;

        /** @name DataContext#layout - Layout settings */
        this.layout = options.layout || {};

        /** @name DataContext#landscape - Disable landscape mode use */
        this.landscape = (this.layout.landscape && this.layout.landscape === true) ? true : false;

        /** @name DataContext#learning - Learning object */
        this.learning = {};

        /** @name DataContext#livereload - Livereload enabled */
        this.livereload = options.livereload || false;

        /** @name DataContext#localStorage - Player use of localStorage (checks also browser support) */
        this.localStorage = Modernizr.localstorage && !!options.localStorage || false;

        /** @name DataContext#logURL - LogLessonProgress URL */
        this.logURL = options.logURL || '';

        /** @name DataContext#mediaURL - Media URL */
        this.mediaURL = options.mediaURL || '';

        /** @name DataContext#mobile - Mobile view settings */
        this.mobile = options.layout.mobile || {};

        /** @name DataContext#modules - Modules to load settings */
        this.modules = options.modules || {};

        /** @name DataContext#navigation - Navigation settings */
        this.navigation = options.navigation || {};

        /** @name DataContext#offlinePackage - Is course an offline package */
        this.offlinePackage = !!options.offlinePackage || false;

        /** @name DataContext#preloadDelay - Delay before following entities content preload */
        this.preloadDelay = options.preloadDelay || 0;

        /** @name DataContext#previewID - Preview entity ID */
        this.previewID = helpers.URL.getParam('contententityid') || '';

        /** @name DataContext#previewMode - Is course in preview mode */
        this.previewMode = !!options.previewMode || false;

        /** @name DataContext#productionMode - Is course in production mode */
        this.productionMode = !!options.productionMode || false;

        /** @name DataContext#scormPackage - Scorm package settings */
        this.scormPackage = options.scormPackage || {};

        /** @name DataContext#setScoreURL - set score URL */
        this.setScoreURL = options.setScoreURL || '';

        /** @name DataContext#setStatusURL - set status URL */
        this.setStatusURL = options.setStatusURL || '';

        /** @name DataContext#statisticsURL - get task statistics URL */
        this.statisticsURL = options.statisticsURL || '';

        /** @name DataContext#svgsInIndexHtml - Is svg in index.html */
        this.svgsInIndexHtml = !!options.svgsInIndexHtml || false;

        /** @name DataContext#swiping - Navigation swiping settings */
        this.swiping = {};

        /** @name DataContext#tokenID - Token ID */
        this.tokenID = helpers.URL.getParam('tokenId') || '';

        /** @name DataContext#task - Tasks settings */
        this.task = options.task || {};

        /** @name DataContext#trainingID - Training ID */
        this.trainingID = options.trainingID || helpers.URL.getParam('id') || '';

        /** @name DataContext#transition - Transition type */
        this.transition = 'none';

        /** @name DataContext#userID - User ID */
        this.userID = options.userID || '';

        /** @name DataContext#version - Version number */
        this.version = '1.81.0' || options.version || 'not specified in config.js';

        // TODO: move to init method
        var debugMode = (Modernizr.sessionstorage) ? window.sessionStorage.getItem('debugMode') : '',
            previewMode = helpers.URL.getParam('previewMode'),
            usePolyfill = (function() {
                if (!Modernizr.dataset || !Modernizr.classlist || !Modernizr.customevent || !Modernizr.promises || !Modernizr.fetch) {
                    return true;
                }

                if (!('forEach' in NodeList.prototype) || !('isArray' in Array) || !('reduce' in Array.prototype) || !('isInteger' in Number) || !('keys' in Object) || !('hasOwnProperty' in Object.prototype)) {
                    return true;
                }

                return false;
            })();

        this.comments = (this.commentTool && this.commentTool.enabled) ? true : false;
        this.expandable = (this.layout.expandable && this.layout.expandable.enabled === true) ? true : false;
        this.expandDelay = (this.layout.expandable && this.layout.expandable.delay) ? options.layout.expandable.delay : 0,
        this.modules.localization = this.modules.localization || false;
        this.modules.required = this.modules.required || [];
        this.modules.optional = this.modules.optional || [];
        this.swiping = this.navigation.swiping || {};
        this.transition = (this.navigation.transition) ? this.navigation.transition.toLowerCase() : '';

        if (debugMode && debugMode.toLowerCase() === 'true') {
            this.debugMode = true;
        }

        if (previewMode && previewMode.toLowerCase() === 'true') {
            this.previewMode = true;
        }

        if (this.diplomaURL && this.trainingID) {
            this.diplomaURL += this.trainingID;
        }

        // if (this.offlinePackage) {
        //     require.config({
        //         urlArgs: 'bust=' + (new Date()).getTime()
        //     });
        // }

        if (usePolyfill) {
            require(['polyfill'], function() {
                _this.init();
            });
        } else {
            _this.init();
        }
    }

    /**
     * Checks if the course is online.
     * @returns {Boolean} true - if not offline package, not preview mode and connection is online
     */
    DataContext.prototype.isCourseOnline = function() {

        return !this.offlinePackage && !this.previewMode && utils.onLine();
    };

    /**
     * Logs progress, sets status and score
     * @param {String} type - Log type (visited, answers, sync)
     * @param {String} [ID = ''] - Entity ID
     */
    DataContext.prototype.logProgress = function(type, ID, counter) {
        var _this = this,
            bodyProgress = {
                lessons: [],
                trainingId: this.trainingID
            },
            bodyStatus = {
                trainingId: this.trainingID
            },
            bodyScore = {
                trainingId: this.trainingID
            },
            training = new Training(),
            userAnswers = new UserAnswers();

        this.setLocal(this.trainingID);

        if (!_this.isCourseOnline()) {
            return;
        }

        var status = userAnswers.getStatus(training.status, training.lastQuizPageID),
            score = userAnswers.getScore(training.score, training.lastQuizPageID);

        switch(type) {
        case 'visited':
            bodyProgress.lessons.push({
                answers: [],
                lessonId: ID
            });
            break;
        case 'answers':
            bodyProgress.lessons.push(userAnswers.mapPageLessons(ID));
            break;
        case 'sync':
            bodyProgress.lessons = (ID) ? userAnswers.mapQuizLessons(ID) : userAnswers.mapCourseLessons();
            break;
        default:
            return;
        }

        var headers = new Headers({
            'Authorization-Token': _this.tokenID,
            'Content-Type': 'application/json'
        });

        logPromise = new Promise(function(resolve, reject) {
            var request = new Request(_this.logURL, {
                    method: 'post',
                    headers: headers,
                    body: JSON.stringify(bodyProgress)
                }),
                promises = [fetch(request), null, null];

            if (status !== training.status) {
                bodyStatus.status = status;

                request = new Request(_this.setStatusURL, {
                    method: 'post',
                    headers: headers,
                    body: JSON.stringify(bodyStatus)
                });

                promises[1] = fetch(request);
            }

            if (score > training.score) {
                bodyScore.score = score;

                request = new Request(_this.setScoreURL, {
                    method: 'post',
                    headers: headers,
                    body: JSON.stringify(bodyScore)
                });

                promises[2] = fetch(request);
            }

            // promises: [logLessonProgress, setStatus, setScore]
            Promise.all(promises).then(function(arr) {
                var error = false,
                    delay = 0;

                if (arr[0]) {
                    if (arr[0].status !== 200) {
                        error = true;
                    }
                }

                if (arr[1]) {
                    if (arr[1].status === 200) {
                        training.setStatus(status);
                    } else {
                        error = true;
                    }
                }

                if (arr[2]) {
                    if (arr[2].status === 200) {
                        training.setScore(score);
                    } else {
                        error = true;
                    }
                }

                if (_this.errorTimeoutID) {
                    window.clearTimeout(_this.errorTimeoutID);
                }

                if (error) {
                    counter = (counter) ? counter + 1 : 1;

                    _this.error = true;

                    if (counter === 4) {
                        delay = 500;
                    } else if (counter === 5) {
                        delay = 1000;
                    } else if (counter > 5) {
                        delay = 2000;
                    }

                    if (counter === 9) {
                        window.dispatchEvent(new Event('log-error'));
                    }

                    _this.errorTimeoutID = setTimeout(function() {
                        _this.logProgress('sync', null, counter);
                    }, delay);
                }

                return 'OK';
            }).then(function(res) {
                resolve(res);
            }).catch(function(err) {
                reject(err);
            });
        });
    }

    /**
     * Gets Entity comments count
     * @param {String}  ID - Entity ID
     * @returns {Promise}
     */
    DataContext.prototype.fetchCommentsCount = function(ID) {
        var _this = this;

        return new Promise(function(resolve, reject) {
            var request,
                requestURL;

            if (!ID || _this.offlinePackage || !_this.previewMode || !_this.commentsCountURL || !utils.onLine()) {
                resolve('-');
                return;
            }

            requestURL = _this.commentsCountURL + '?id=' + ID;

            request = new Request(requestURL, {
                method: 'get',
                headers: new Headers({
                    'Authorization-Token': _this.tokenID
                })
            });

            fetch(request).then(function(res) {
                return res.json();
            }).then(function(res) {
                resolve(res);
            }).catch(function(err) {
                reject(err);
            });
        });
    }

    /**
     * Gets task social statistics
     * @param {Object}  options
     * @param {Boolean} [options.includeUserData = false] - Include user data in response
     * @param {Boolean} [options.inClass = false] - Set results scope to current class
     * @param {Integer} [options.maxAnswers = 25] - Maximum number of users answers
     * @param {Integer} [options.maxAnswers = 1024] - Maximum length of a user answer
     * @param {Integer} [options.order = 0] - Order type of user answers
     * @param {String}  options.taskID - Task ID
     * @returns {Promise}
     */
    DataContext.prototype.getStatistics = function(options) {
        options = options || {};

        var _this = this,
            includeUserData = options.includeUserData || false,
            classID = (options.inClass) ? this.classID : '',
            maxAnswers = options.maxAnswers || 25,
            maxAnswerLength = options.maxAnswerLength || 1024,
            order = options.order || 0,
            URL = this.statisticsURL + '?TaskId=' + options.taskID + '&MaxAnswers=' + maxAnswers + '&MaxAnswerLength=' + maxAnswerLength + '&IncludeUserData=' + includeUserData + '&Order=' + order;

        if (classID) {
            URL += '&ClassId=' + classID;
        }

        return new Promise(function(resolve, reject) {
            var request = new Request(URL, {
                method: 'get',
                headers: new Headers({
                    'Authorization-Token': _this.tokenID
                })
            });

            if (!_this.isCourseOnline()) {
                resolve(null);
                return;
            }

            logPromise.then(function() {
                fetch(request).then(function(res) {
                    return res.json();
                }).then(function(res) {
                    resolve(res);
                }).catch(function(err) {
                    reject(err);
                });
            });
        });
    };

    /**
     * Enables live reload.
     * @param {String} ID - Entity ID
     */
    DataContext.prototype.watch = function(ID) {
        var _this = this;

        if (!_this.livereload || !_this.previewMode) {
            return;
        }

        ID = ID || _this.learning.current.contentEntityId;

        require(['live-reload'], function(LiveReload) {
            var liveReload = new LiveReload({
                ID: ID,
                entityURL: _this.entityURL,
                tokenID: _this.tokenID
            });

            liveReload.watch(ID);
        })
    };

    DataContext.prototype.init = function() {
        var _this = this,
            conf = {
                classID: this.classID,
                dataURL: this.dataURL,
                debugMode: this.debugMode,
                localStorage: this.localStorage,
                modules: {
                    localization: this.modules.localization,
                    optional: this.modules.optional,
                    required: this.modules.required
                },
                offlinePackage: this.offlinePackage,
                onLine: utils.onLine(),
                previewMode: this.previewMode,
                scormProp: this.scormPackage,
                tokenID: this.tokenID,
                trainingID: this.trainingID
            };

        if (!this.offlinePackage && !this.trainingID) {

            if (this.debugMode) {
                console.error('training ID is not defined');
            }

            return;
        }

        if (!utils.JSON()) {
            if (this.debugMode) {
                console.error('JSON parse is not supported');
            }

            return;
        }

        if (this.debugMode) {
            console.log('player version - ' + this.version);
            console.log('productionMode - ' + this.productionMode);
        }


        // Move to get Learning, after resolve only complete fn should remain
        Promise.all([getLearning(conf), loadModules({modules: _this.modules.required})]).then(function(arr) {
            var res = (Array.isArray(arr) && arr[0]) ? arr[0] : null,
                complete = function(res) {
                    _this.setLearning(res.learning);

                    _this.setLocal(res.trainingID);

                    window.initPageRender = new Date();

                    bindViewModel(_this);

                    loadModules({modules: _this.modules.optional});
                };

            if (!res || !res.learning || !res.trainingID) {
                if (_this.debugMode) {
                    console.error('no data loaded');
                }
                return;
            }

            if (!res.learning.lang || _this.modules.localization) {
                require(['custom-lang'], function(lang) {
                    var setLanguage = function(res, lang) {
                        if (!res.learning.lang) {
                            res.learning.lang = lang;
                            return res;
                        }

                        for (var i in lang) {
                            if (lang.hasOwnProperty(i) && !res.learning.lang.hasOwnProperty(i)) {
                                res.learning.lang[i] = lang[i];
                            }
                        }

                        return res;
                    };

                    res = setLanguage(res, lang)

                    complete(res);
                });
            } else {

                if (_this.debugMode) {
                    console.error('learning lang is undefined');
                }

                res.learning.lang = {};

                complete(res);
            }

        }).catch(function(error) {
            if (_this.debugMode) {
                console.error(error);
            }
        });
    };

    DataContext.prototype.setLearning = function(obj) {
        var _this = this;

        if (obj && typeof obj === 'object' && Object.keys(obj).length !== 0) {
            _this.setTraining(obj);
            _this.setUser(obj);
            _this.setLanguage(obj);

            _this.learning.current = helpers.content.cloneInitEntity(_this.learning.training, _this.learning.user, _this.previewMode, _this.previewID);

            if (!_this.learning.current) {
                return;
            }

            var ID = _this.learning.current.ID,
                training = new Training();

            store.dispatch({
                type: 'SET_TRAINING',
                training: training,
                isDebugMode: _this.debugMode
            });

            training.setEntityID(ID);

            store.dispatch({
                type: 'SET_ENTITY',
                payload: {
                    ID: ID
                }
                // entity: training.getEntity(ID)
            });
        }
    };

    DataContext.prototype.setTraining = function(obj) {
        var _this = this,
            training = new Training({
                dateModified: _this.dateModified,
                entityURL: _this.entityURL,
                mediaURL: _this.mediaURL,
                offlinePackage: _this.offlinePackage,
                scormPackage: _this.scormPackage.enabled,
                previewMode: _this.previewMode,
                state: 'init',
                tokenID: _this.tokenID
            }),
            options = _this.connection || {},
            content;

        options.dataContext = this;

        if (obj.training && typeof obj.training === 'object' && Object.keys(obj.training).length !== 0) {

            content = obj.training;

            // TODO: remove
            if(obj.training.contentEntities) {
                obj.training.contentEntities.forEach(function(entry) {
                    parseComponents(entry);
                });
            }
            // TODO: END remove

            _this.learning.training = content;

            if (content.id) {
                _this.trainingID = content.id;
            }

            training.setProperties({
                componentsJSON: content.componentsJson,
                contentEntities: content.contentEntities,
                containerID: content.contentContainerId,
                enforceSequence: content.enforceSequence,
                svgsInIndexHtml: _this.svgsInIndexHtml,
                images: content.images,
                dateModified: content.dateModified,
                imagePosition: content.imagePosition,
                imageScaleMode: content.imageScaleMode
            });
        }

        new Navigation(_this.navigation);

        new Notification(options);

        // TODO: remove
        function parseComponents(entry){
            if(entry.contentEntity.componentsJson != undefined && entry.contentEntity.componentsJson != "") {
                var comps = JSON.parse(entry.contentEntity.componentsJson);
                entry.contentEntity.components = helpers.content.convertComponents(comps);
            }
            if(entry.children){
                entry.children.forEach(function (subEntry) {
                    parseComponents(subEntry);
                });
            }
        }
        // TODO: END remove
    };

    DataContext.prototype.setLocal = function(trainingID) {
        var _this = this,
            learning = {
                lang: _this.learning.lang,
                training: _this.learning.training,
                user: _this.learning.user
            },
            clearLocal = function() {
                var key,
                    regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/,
                    regexUser = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

                if (_this.debugMode) {
                    console.log('localStorage cleared (' + utils.localStorageSpace().toFixed(2) + ' KB)');
                }

                for (key in localStorage) {
                    if (localStorage[key] && (key.match(regex) || key.match(regexUser))) {
                        window.localStorage.removeItem(key);
                    }
                }
            };

        if (trainingID && this.localStorage) {

            setTimeout(function() {
                var str = JSON.stringify(learning),
                    usr = JSON.stringify(_this.learning.user);

                try {
                    if (utils.localStorageSpace() > 4000) { // 100 pages/ tasks - 1690 KB
                        clearLocal();
                    }

                    window.localStorage.setItem(trainingID, str);

                    if (_this.userID) {
                        window.localStorage.setItem(trainingID + '-' + _this.userID, usr);
                    }
                } catch(e) {
                    if (_this.debugMode) {
                        console.error(e);
                    }
                }

            }, 10);
        }
    };

    DataContext.prototype.setUser = function(obj) {
        var _this = this,
            userAnswers = new UserAnswers();

        if (obj.hasOwnProperty('user') && obj.user && typeof obj.user === 'object' && Object.keys(obj.user).length !== 0) {
            _this.learning.user = obj.user;

            if (obj.user.hasOwnProperty('answers') && Array.isArray(obj.user.answers)) {
                _this.learning.user.progress = _this.learning.user.answers;

                userAnswers.setUserAnswers(_this.learning.user.answers);
            }

            if (obj.user.hasOwnProperty('id') && obj.user.id) {
                _this.userID = obj.user.id;
            }

            store.dispatch({
                type: 'SET_USER',
                user: obj.user
            });
        }

    };

    DataContext.prototype.setLanguage = function(obj) {
        var _this = this,
            notification = new Notification();

        if (obj.hasOwnProperty('lang') && obj.lang && typeof obj.lang === 'object') {
            _this.learning.lang = obj.lang;

            notification.setLang(obj.lang);
        }
    };

    return function(config) {

        if (!dataContext) {
            dataContext = new DataContext(config);
        }

        return dataContext;
    };

});

